import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { CreditCards } from '@df/session/api/credit-cards/credit-cards.namespace';

import find from 'lodash-es/find';
import { Nexus } from '@tomandco/nexus-api-types';

export const cardTypesConfig: CreditCards.IConfig[] = [
    {
        id: 'VI',
        rule: /^4\d{3}([\ \-]?)(?:\d{4}\1){2}\d(?:\d{3})?$/,
        label: 'Visa'
    },
    {
        id: 'MC',
        rule: /^5[1-5]\d{2}([\ \-]?)\d{4}\1\d{4}\1\d{4}$/,
        label: 'MasterCard'
    },
    {
        id: 'MAESTRO',
        rule: /^(?:5[0678]\d\d|6304|6390|67\d\d)\d{8,15}$/,
        label: 'Maestro'
    },
    {
        id: 'UKE',
        rule: /^(4026|417500|4508|4844|491(3|7))$/,
        label: 'Visa Electron'
    },
    { id: 'AE', rule: /^3[47][0-9]{13}$/, label: 'American Express' }
];

/**
 * Credit card model representing token saved in magento
 */
export class CreditCard implements Nexus.Lib.Checkout.PaymentMethodCard {
    isCard: true = true;

    /**
     * Status object for card removal
     * @type {Status}
     */
    removeStatus = new Status();

    /**
     * Status object for setting cart default
     * @type {Status}
     */
    setCardDefaultStatus = new Status();

    /**
     * Request service
     */
    @NgInject(RequestService)
    protected requestService!: RequestService;

    constructor(public data: CreditCards.IData) {}

    /**
     * Returns card type by card number
     * @param {string | number} cardNumber
     * @returns {string | undefined}
     */
    static matchNumber(cardNumber: string | number): string | undefined {
        const number = cardNumber.toString().replace(/\s/g, '');
        const type = find(cardTypesConfig, config => !!number.match(config.rule));
        return type?.id;
    }

    /**
     * Returns card type option by type id
     * @param {string} id
     * @returns {CreditCards.IConfig}
     */
    static getTypeById(id: string): CreditCards.IConfig | undefined {
        id = id.toLowerCase().trim();
        return find(cardTypesConfig, cardType => cardType.id.toLowerCase() === id);
    }

    /**
     * Returns card type options for given allowed types
     * @param {string[]} allowedTypes
     * @returns {CreditCards.ITypeOption[]}
     */
    static getCardTypes(allowedTypes: string[]): CreditCards.ITypeOption[] {
        return allowedTypes.map(allowedType => {
            const typeConfig = CreditCard.getTypeById(allowedType);
            return {
                value: allowedType,
                label: typeConfig?.label || allowedType
            };
        });
    }

    /**
     * Remove token
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    remove() {
        return this.requestService.send(this.removeStatus, {
            path: '/customer/removeSavedCard',
            data: this.getRequestData()
        });
    }

    /**
     * Make default credit card
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    setDefault() {
        return this.requestService.send(this.setCardDefaultStatus, {
            path: '/customer/setdefaultsavedcard',
            data: this.getRequestData()
        });
    }

    private getRequestData(): CreditCards.IRequestData {
        const data: CreditCards.IRequestData = {
            method: this.data.method
        };
        if (this.data.public_hash) {
            data.public_hash = this.data.public_hash;
        } else {
            data.id = this.data.id;
        }
        return data;
    }

    get title(): string {
        return `**** **** **** ${this.data.last_four}`;
    }

    get code(): string {
        return `${this.data.public_hash || this.data.id}${this.data.method || ''}`;
    }

    get method(): string {
        return this.data.method;
    }

    get typeLabel(): string | undefined {
        switch (this.data.type?.toLowerCase()) {
            case 'vi':
            case 'visa':
                return 'Visa';

            case 'mc':
            case 'mastercard':
                return 'Mastercard';

            case 'ae':
            case 'amex':
                return 'American Express';

            case 'maestro':
                return 'Maestro';

            case 'uke':
                return 'Visa Electron';

            default:
                return undefined;
        }
    }

    get isExpired() {
        const expiry_date = this.data.expiry_date;
        if (!expiry_date) {
            return false;
        }
        const [month, year] = expiry_date.split('/').map(i => parseInt(i, 10));
        const date = new Date();
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth() + 1;
        const thousands = Math.floor(currentYear / 1000) * 1000;
        const fullYear = year < thousands ? year + thousands : year;

        return fullYear < currentYear || (fullYear == currentYear && month <= currentMonth);
    }

    isCcvRequired(): boolean {
        return !!this.data.ccv_is_required;
    }

    get type(): string {
        const type = this.data.type;
        return type ? type.toLowerCase() : '';
    }
}
